import cn from 'classnames';
import { FC, useMemo } from 'react';
import { AiOutlineLeft, AiOutlineRight } from 'react-icons/ai';
import { pageKey, pageSizeKey } from '../../helpers/path';
import { useLang } from '../../hooks/use-lang';
import { useQueryParams } from '../../hooks/use-query-params';
import { PaginationType } from '../../types/pagination';
import { Button } from '../button/button';
import { Select } from '../select/select';
import s from './pagination.module.css';

export interface PaginationProps {
  align?: 'start' | 'center' | 'end';
  className?: string;
  current?: number;
  disabled?: boolean;
  pageSize?: number;
  pageSizeOptions?: number[];
  showTotal?: boolean;
  showSizeChanger?: boolean;
  total?: number;
  pagePrefix?: string;
  pagination?: PaginationType;
}

const viewportPages = 5;
const viewportPagesOffset = 3;

export const Pagination: FC<PaginationProps> = (props) => {
  const {
    align = 'end',
    disabled = false,
    pageSizeOptions = [10, 20, 50, 100],
    showTotal = true,
    showSizeChanger = true,
    total = 0,
    className,
    pagePrefix,
    pagination,
  } = props;

  const { t } = useLang();
  const { addQueryParams, getQueryParams } = useQueryParams();

  const resultPageKey = pagePrefix ? pagePrefix + pageKey : pageKey;
  const resultPageSizeKey = pagePrefix ? pagePrefix + pageSizeKey : pageSizeKey;

  const page = Number(getQueryParams()[resultPageKey] || '1');
  const pageSize = Number(getQueryParams()[resultPageSizeKey] || '10');

  const onPaginationChange = (page: number | string, size: number | string) => {
    addQueryParams({ [resultPageKey]: `${page}`, [resultPageSizeKey]: `${size}` });
  };

  const { isRenderPagination, start, end, pagesCount, pages } = useMemo(() => {
    if (pagination) {
      const pagesCount = pagination.total_pages;
      const showStart = pagesCount > viewportPages && page - viewportPagesOffset >= 0;
      const showEnd = pagesCount > viewportPages;
      const isCurrentAtTheEnd = pagesCount - (page - viewportPagesOffset) < viewportPages;

      const start = showStart
        ? isCurrentAtTheEnd
          ? pagesCount - viewportPages
          : page - viewportPagesOffset
        : 0;
      const end = showEnd ? start + viewportPages : pagesCount;
      const isRenderPagination =
        pagination.total_pages > 1 || pagination.count > pagination.page_size;
      const pages = [...Array(pagesCount).keys()];
      return { isRenderPagination, start, end, pagesCount, pages };
      // Все что ниже - фронтовая реализация пагинации, её потом удалим
    }
    const pagesCount = total ? Math.ceil(total / pageSize) : 1;
    const showStart = pagesCount > viewportPages && page - viewportPagesOffset >= 0;
    const showEnd = pagesCount > viewportPages;
    const isCurrentAtTheEnd = pagesCount - (page - viewportPagesOffset) < viewportPages;

    const start = showStart
      ? isCurrentAtTheEnd
        ? pagesCount - viewportPages
        : page - viewportPagesOffset
      : 0;
    const end = showEnd ? start + viewportPages : pagesCount;
    const isRenderPagination = total > pageSize;
    const pages = [...Array(pagesCount).keys()];
    return { isRenderPagination, start, end, pagesCount, pages };
  }, [page, pageSize, total, pagination]);

  if (!isRenderPagination) return null;

  return (
    <div className={cn(s.component, className, s[align])}>
      {showTotal && t('Всего записей: {{count}}', { count: pagination ? pagination.count : total })}
      <Button
        className={s.pageButton}
        variant="secondary"
        disabled={disabled || page == 1}
        onClick={() => onPaginationChange?.(page - 1, pageSize)}
        data-testid="prev-button"
      >
        <AiOutlineLeft />
      </Button>
      {start !== 0 && (
        <Button
          className={s.pageButton}
          variant="secondary"
          onClick={() => onPaginationChange?.(Math.ceil(start / 2), pageSize)}
        >
          ...
        </Button>
      )}
      {pages.slice(start, end).map((pageNumber) => (
        <Button
          key={pageNumber}
          variant={page === pageNumber + 1 ? 'primary' : 'secondary'}
          onClick={() => onPaginationChange?.(pageNumber + 1, pageSize)}
        >
          {pageNumber + 1}
        </Button>
      ))}
      {end < pagesCount && (
        <Button
          className={s.pageButton}
          variant="secondary"
          onClick={() =>
            onPaginationChange?.(Math.ceil(pagesCount - (pagesCount - end) / 2), pageSize)
          }
        >
          ...
        </Button>
      )}
      <Button
        className={s.pageButton}
        variant="secondary"
        disabled={disabled || page == pagesCount}
        onClick={() => onPaginationChange?.(page + 1, pageSize)}
        data-testid="next-button"
      >
        <AiOutlineRight />
      </Button>
      {showSizeChanger && (
        <Select
          options={pageSizeOptions.map((pageSizeOption) => ({
            id: `${pageSizeOption}`,
            value: `${pageSizeOption}/${t('страница')}`,
          }))}
          className={s.pageSizeSelector}
          value={{ id: `${pageSize}`, value: `${pageSize}/${t('страница')}` }}
          onSelect={(val) => onPaginationChange?.(1, val.id)}
          position="top"
        />
      )}
    </div>
  );
};
